import React, { useContext, useMemo, useRef } from 'react';
import invariant from 'tiny-invariant';

/**
 * Sync a given state (or any value, but it won't react to changes) to a ref.
 */
export const useStateRef = <T>(value: T) => {
  const ref = useRef(value);

  useMemo(() => {
    ref.current = value;
  }, [value]);

  return ref;
};

export const createSimpleContextWithNullSafeHook = <T>(
  displayName: string,
  defaultValue: T | null = null,
) => {
  const Context = React.createContext(defaultValue);

  Context.displayName = displayName;

  const hook = () => {
    const ctx = useContext(Context);
    const prev = useRef(ctx);

    useMemo(() => {
      prev.current = ctx;

      return ctx;
    }, [ctx]);

    invariant(
      ctx != null,
      `${displayName} provider must be present in the tree.`,
    );

    return ctx;
  };

  return [Context, hook] as const;
};

export type SuspensablePromise<T> = Promise<T> & {
  status?: 'fulfilled' | 'rejected' | 'pending';
  value?: T;
  reason?: unknown;
};

export const createFulfilledSuspensablePromise = <T>(
  value: T,
): SuspensablePromise<T> => {
  const promise = Promise.resolve(value) as SuspensablePromise<T>;

  promise.status = 'fulfilled';
  promise.value = value;

  return promise;
};
